import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Menu, LogOut, User, Search, Bell, ChevronDown } from "lucide-react";
import { isAuth, signout } from "../../actions/auth";
import Notify from "../customHelpers/Notify";
import Notifications from "../../actions/notification";
import "./topnav.css";
import { BellRing } from "lucide-react";
import { UserPlus } from "lucide-react";
import {
  LayoutDashboard,
  Package,
  Users,
  Settings,
  Tags,
  Container,
  ShoppingBag,
  Boxes,
  AlertTriangle,
  BarChart2,
  FileText,
  Archive,
  Activity,
  CreditCard,
  Truck,
  Shield,
  UsersRound,
} from "lucide-react";

const admin_routes = [
  {
    display_name: "Dashboard",
    route: "/",
    icon: <LayoutDashboard />,
  },
  {
    display_name: "Purchase Orders",
    route: "/purchase-order",
    icon: <ShoppingBag />,
  },
  {
    display_name: "Inventories",
    icon: <Boxes />,
    subRoutes: [
      {
        display_name: "Categories",
        route: "/categories",
        icon: <Tags />,
      },
      {
        display_name: "Inventories",
        route: "/products",
        icon: <Package />,
      },
      {
        display_name: "Containers",
        route: "/containers",
        icon: <Container />,
      },
    ],
  },
  {
    display_name: "Customers",
    route: "/customers",
    icon: <UserPlus />,
  },
  {
    display_name: "Reports",
    icon: <AlertTriangle />,
    subRoutes: [
      {
        display_name: "Sales Report",
        route: "/sales-reports",
        icon: <BarChart2 />,
      },
      {
        display_name: "Sales Detail Report",
        route: "/detail-reports",
        icon: <FileText />,
      },
      {
        display_name: "Inventory Report",
        route: "/inventory",
        icon: <Archive />,
      },
      {
        display_name: "Inventory Report By Container",
        route: "/container-report",
        icon: <Container />,
      },
      {
        display_name: "Activity Log Report",
        route: "/activity-log",
        icon: <Activity />,
      },
      {
        display_name: "Payment Report",
        route: "/payment-report",
        icon: <CreditCard />,
      },
      {
        display_name: "Purchase Order Report",
        route: "/purchase-container",
        icon: <Truck />,
      },
    ],
  },
  {
    display_name: "Staffs",
    icon: <Users />,
    subRoutes: [
      {
        display_name: "Roles",
        route: "/roles",
        icon: <Shield />,
      },
      {
        display_name: "Staffs",
        route: "/staffs",
        icon: <UsersRound />,
      },
    ],
  },
  {
    display_name: "General Settings",
    route: "/settings",
    icon: <Settings />,
  },
  {
    display_name: "Shipment",
    route: "/shipment-order",
    icon: <ShoppingBag />,
  },
  {
    display_name: "Sales Order",
    route: "/orders",
    icon: <ShoppingBag />,
  },
];

const superadmin_routes = [
  {
    display_name: "Dashboard",
    route: "/admin",
    icon: <LayoutDashboard />,
  },
  {
    display_name: "Organization",
    route: "/admin/organization",
    icon: <Settings />,
  },
  {
    display_name: "Organization admin",
    route: "/admin/staff",
    icon: <Users />,
  },
  {
    display_name: "Register",
    route: "/admin/signup",
    icon: <UserPlus />,
  },
  {
    display_name: "Subscriptions",
    route: "/admin/subscriptions",
    icon: <BellRing />,
  },
];

const Topnav = ({ handleDrawerToggle, sidebarOpen, collapsed }) => {
  const history = useHistory();
  const [count, setCount] = useState(
    parseInt(localStorage.getItem("notificationCount")) || 0
  );
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const { NotifyMessage } = Notify();
  const [loading, setLoading] = useState(false);
  const [notification, setNotifications] = useState([]);
  const { viewAllNotifications } = Notifications();
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    const fetchNotificationCount = () => {
      setCount(parseInt(localStorage.getItem("notificationCount")) || 0);
    };

    fetchNotificationCount();
    const intervalId = setInterval(fetchNotificationCount, 100);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!searchValue.trim()) {
      setSearchResults([]);
      return;
    }

    const allRoutes =
      isAuth().role === "superadmin" ? superadmin_routes : admin_routes;
    const results = [];

    allRoutes.forEach((route) => {
      if (
        route.display_name.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        results.push({ display_name: route.display_name, route: route.route });
      }
      if (route.subRoutes) {
        route.subRoutes.forEach((subRoute) => {
          if (
            subRoute.display_name
              .toLowerCase()
              .includes(searchValue.toLowerCase())
          ) {
            results.push({
              display_name: subRoute.display_name,
              route: subRoute.route,
            });
          }
        });
      }
    });

    setSearchResults(results);
  }, [searchValue]);

  const handleNotificationClick = () => {
    setShowNotifications(true);
    setLoading(true);
    viewAllNotifications().then((data) => {
      if (data.err) {
        NotifyMessage({ message: data.err, type: "error" });
      } else if (data.notifications) {
        setLoading(false);
        setNotifications(data.notifications);
      }
    });
    localStorage.setItem("notificationCount", "0");
  };

  const getInitials = (name) => {
    if (!name) return "";
    const nameParts = name.split(" ");
    if (nameParts.length < 2) return nameParts[0].charAt(0).toUpperCase();
    return (
      nameParts[0].charAt(0).toUpperCase() +
      nameParts[1].charAt(0).toUpperCase()
    );
  };

  const handleSearchResultClick = (route) => {
    setSearchValue("");
    setSearchResults([]);
    history.push(route);
  };

  const getTopnavClass = () => {
    if (isMobile) {
      return sidebarOpen ? "topnav topnav--sidebar-mobile-open" : "topnav";
    }

    if (sidebarOpen) {
      return collapsed
        ? "topnav topnav--sidebar-collapsed"
        : "topnav topnav--sidebar-expanded";
    }

    return "topnav";
  };

  return (
    <header className={getTopnavClass()}>
      <div className="topnav__left">
        <button
          onClick={handleDrawerToggle}
          className="topnav__menu-button"
          aria-label="Toggle menu"
        >
          <Menu className="topnav__icon" />
        </button>
        {isAuth().role !== "superadmin" && isAuth()?.staffOrganizations && (
          <span className="topnav__org-name hidden sm:block">
            Organization:{" "}
            {isAuth()?.staffOrganizations[0]?.organization?.organization_name}
          </span>
        )}
      </div>
      <div className="topnav__right">
        <div className="topnav__search hidden md:flex">
          <div className="topnav__search-container">
            <Search className="topnav__search-icon" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search routes..."
              className="topnav__search-input"
            />
            {searchResults.length > 0 && (
              <div className="topnav__search-dropdown">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="topnav__search-result"
                    onClick={() => handleSearchResultClick(result.route)}
                  >
                    {result.display_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <button className="topnav__mobile-search md:hidden">
          <Search className="topnav__icon" />
        </button>
        {isAuth().role !== "superadmin" && (
          <div className="topnav__notifications">
            <button
              onClick={handleNotificationClick}
              className="topnav__notification-button"
              aria-label="Notifications"
            >
              <Bell className="topnav__icon" />
              {count > 0 && (
                <span className="topnav__notification-badge">
                  {count > 9 ? "9+" : count}
                </span>
              )}
            </button>
            {showNotifications && (
              <>
                <div
                  className="topnav__overlay"
                  onClick={() => setShowNotifications(false)}
                />
                <div className="topnav__notification-dropdown">
                  <div className="topnav__notification-header">
                    <h3 className="topnav__notification-title">
                      Notifications
                    </h3>
                    {count > 0 && (
                      <span className="topnav__notification-count">
                        {count} new
                      </span>
                    )}
                  </div>
                  <div className="topnav__notification-list">
                    {loading ? (
                      <div className="topnav__notification-loading">
                        Loading...
                      </div>
                    ) : notification.length > 0 ? (
                      notification.map((note) => (
                        <div
                          key={note._id}
                          className="topnav__notification-item"
                        >
                          <div className="topnav__notification-content">
                            <p className="topnav__notification-title truncate">
                              {note.title}
                            </p>
                            <p className="topnav__notification-message">
                              {note.message}
                            </p>
                            <p className="topnav__notification-date">
                              {new Date(note.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="topnav__notification-empty">
                        No notifications
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <div className="topnav__profile">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="topnav__profile-button"
          >
            <span className="topnav__profile-name">{isAuth().fullName}</span>
            <div className="topnav__avatar">
              <span className="topnav__avatar-text">
                {getInitials(isAuth().fullName)}
              </span>
            </div>
            <ChevronDown className="topnav__icon" />
          </button>
          {showProfileMenu && (
            <>
              <div
                className="topnav__overlay"
                onClick={() => setShowProfileMenu(false)}
              />
              <div className="topnav__profile-dropdown">
                <div className="topnav__profile-info">
                  <div className="topnav__avatar large">
                    <span className="topnav__avatar-text">
                      {getInitials(isAuth().fullName)}
                    </span>
                  </div>
                  <div>
                    <p className="topnav__profile-name">{isAuth().fullName}</p>
                    <p className="topnav__profile-email">{isAuth().email}</p>
                  </div>
                </div>
                <div className="topnav__profile-menu">
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      history.push(
                        isAuth().role !== "superadmin"
                          ? "/profile"
                          : "/admin/profile"
                      );
                    }}
                    className="topnav__profile-menu-item"
                  >
                    <User className="topnav__icon" />
                    View Profile
                  </button>
                </div>
                <hr className="topnav__divider" />
                <div className="topnav__profile-menu">
                  <button
                    onClick={() => signout(() => history.replace("/signin"))}
                    className="topnav__profile-menu-item logout"
                  >
                    <LogOut className="topnav__icon" />
                    Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topnav;
